import React, { useState } from 'react'
import { batch } from '@preact/signals-react'
import { Box, Button, Popover, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { dsColorNew } from '@palette'
import { isLoadingCommonFilters, isOfficeGroup } from '@signals'
import { areCommonFiltersValid } from '@signals/commonFilters/filters'
import { BIAggregationPrice, BIAsked, BIFormData, BIGoalMissing, BIOptionLabels, BISelectedOffice, setFormData } from './@signals'
import { buildSentence, changedKeys } from './@services/sentence'
import { isStale } from './@services/snapshot'


const AMBER_TEXT = dsColorNew.noteAmber

export const tileSx = (size = 32) => ({
	width: size, height: size, flex: 'none', borderRadius: '8px',
	display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
	backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue,
})

const pillSx = tone => ({
	display: 'inline-flex', alignItems: 'center', px: 1.25, py: '1px', borderRadius: '999px', fontSize: 12, fontWeight: 500,
	textTransform: 'none', letterSpacing: 0, whiteSpace: 'nowrap',
	...{
		neutral: { backgroundColor: '#F3F4F6', color: dsColorNew.textDark },
		info: { backgroundColor: dsColorNew.noteInfoBg, color: dsColorNew.noteInfo },
		err: { backgroundColor: dsColorNew.dangerBg, color: dsColorNew.dangerRed },
	}[tone],
})

const kindSx = { display: 'flex', alignItems: 'center', gap: 1, fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle, mb: '2px' }

/** The segments of a sentence as text: strong in title colour, "all" in grey, gaps in amber, many values behind a list */
function Sentence({ segments, highlight = [], onList, sx = {} }) {
	return <Typography component="div" sx={{ fontSize: 14.5, lineHeight: 1.55, color: dsColorNew.textDark, ...sx }}>
		{segments.map(seg => <React.Fragment key={seg.key}>
			{seg.before}
			<Box component="span" sx={highlight.includes(seg.key) ? { backgroundColor: '#FEF3C7', borderRadius: '3px', px: '2px' } : {}}>
				{seg.pieces.map((p, i) => {
					if (p.list) return <Box key={i} component="span" role="button" tabIndex={0}
						onClick={e => onList?.(e.currentTarget, p)} onKeyDown={e => e.key === 'Enter' && onList?.(e.currentTarget, p)}
						sx={{ color: dsColorNew.textTitle, fontWeight: 600, borderBottom: `1px dotted ${dsColorNew.textMuted}`, cursor: 'pointer', '&:hover': { color: dsColorNew.appBlue } }}>{p.t}</Box>
					if (p.kind === 'strong') return <Box key={i} component="span" sx={{ color: dsColorNew.textTitle, fontWeight: 600 }}>{p.t}</Box>
					if (p.kind === 'muted') return <Box key={i} component="span" sx={{ color: dsColorNew.textMuted }}>{p.t}</Box>
					if (p.kind === 'gap') return <Box key={i} component="span" sx={{ color: AMBER_TEXT, fontWeight: 600, borderBottom: `1.5px dashed ${dsColorNew.warningBar}` }}>{p.t}</Box>
					return <React.Fragment key={i}>{p.t}</React.Fragment>
				})}
			</Box>
		</React.Fragment>)}
	</Typography>
}

/** The sentence of what the results answer, or would answer (CU-869fmk5ge piece 2, CA 9-14) */
export default function BIQuerySentence() {
	const [showNext, setShowNext] = useState(false)
	const [list, setList] = useState(null)

	const asked = BIAsked.value
	const form = BIFormData.value
	const live = buildSentence({ form, labels: BIOptionLabels.value, choose: BIGoalMissing.value, isOfficeGroup: isOfficeGroup.value })
	const onList = (el, piece) => setList({ el, piece })

	let label = 'Your question', pill = { tone: 'neutral', text: 'Not searched yet' }, shown = live, stale = false
	if (asked) {
		const stat = asked.phase === 'done' ? BIAggregationPrice.value : null
		shown = buildSentence({ form: asked.form, labels: asked.labels, stat, selectedOffice: asked.selectedOffice })
		stale = asked.phase !== 'searching' && isStale(asked.form, form)
		if (asked.phase === 'searching') { label = 'Searching'; pill = { tone: 'info', text: 'Searching…' } }
		else if (asked.phase === 'failed') { label = 'Your question'; pill = { tone: 'err', text: 'Search failed' } }
		else { label = 'Showing'; pill = null }
	}
	const ready = areCommonFiltersValid.value && !BIGoalMissing.value?.length
	const search = () => batch(() => {
		BISelectedOffice.value = null
		isLoadingCommonFilters.value = true
		setShowNext(false)
	})

	return <Box data-tour="bi-question" sx={{ backgroundColor: '#fff', border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '16px', boxShadow: '0 1px 2px rgba(12,26,62,.04)', px: 2, py: 1.75, mb: 2 }}>
		<Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
			<Box sx={{ ...tileSx(32), mt: '1px' }}><IconSeeker icon="circle-question" sx={{ fontSize: 14 }}/></Box>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Box sx={kindSx}>{label}{pill && <Box component="span" sx={pillSx(pill.tone)}>{pill.text}</Box>}</Box>
				<Sentence segments={shown} onList={onList}/>
			</Box>
		</Box>

		{stale && <>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', ml: 5.5, mt: 1.25, pt: 1.25, borderTop: `1px dashed ${dsColorNew.noteAmberBorder}`, fontSize: 13, color: AMBER_TEXT }}>
				<IconSeeker icon="triangle-exclamation" sx={{ fontSize: 13, color: AMBER_TEXT }}/>
				<Typography component="span" sx={{ fontSize: 13, color: AMBER_TEXT }}>
					<b>You changed the query</b> — the numbers below still answer the question above. Search to update.
				</Typography>
				<Box sx={{ flex: 1 }}/>
				<Button size="small" variant="text" onClick={() => setShowNext(v => !v)} sx={{ textTransform: 'none', fontSize: 13 }}>
					{showNext ? 'Hide the new question' : 'See the new question'}
				</Button>
				<Button size="small" variant="outlined" onClick={() => { setFormData({ ...asked.form, urlId: form.urlId }); setShowNext(false) }} sx={{ textTransform: 'none', fontSize: 13, height: 32 }}>
					Undo changes
				</Button>
				<Button size="small" variant="contained" disableElevation disabled={!ready} onClick={search} startIcon={<IconSeeker icon="magnifying-glass" sx={{ fontSize: 12 }}/>} sx={{ textTransform: 'none', fontSize: 13, height: 32 }}>
					Search
				</Button>
			</Box>
			{showNext && <Box sx={{ ml: 5.5, mt: 1 }}>
				<Box sx={kindSx}>Next search</Box>
				<Sentence segments={live} highlight={changedKeys(buildSentence({ form: asked.form, labels: asked.labels, isOfficeGroup: isOfficeGroup.value }), live)} onList={onList} sx={{ fontSize: 13.5 }}/>
			</Box>}
		</>}

		<Popover open={!!list} anchorEl={list?.el} onClose={() => setList(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
			{list && <Box sx={{ p: 1.5, maxHeight: 320, overflow: 'auto', fontSize: 13, color: dsColorNew.textDark }}>
				<Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>{list.piece.list.items.length} {list.piece.list.noun}</Typography>
				{list.piece.list.items.map(item => <Typography key={item} sx={{ fontSize: 13, lineHeight: 1.6 }}>{item}</Typography>)}
			</Box>}
		</Popover>
	</Box>
}
